/**
 * Pure duplicate-pair logic (no Angular). Shared by main thread fallback and Web Worker.
 */

export type DuplicatePairKind = 'invite-invite' | 'invite-file' | 'file-file';

export interface DuplicateMatchPatientPayload {
  id?: string;
  active?: boolean;
  status?: string;
  name?: string;
  surname?: string;
  email?: string;
  mobileNumber?: string;
  dob?: string;
  gender?: string;
  patientNumber?: string;
  diagnosis?: string;
  idNumber?: string;
  passportNumber?: string;
  /** ISO string from API; used for duplicate list display only */
  createdAt?: string;
}

export interface DuplicateMatchDetail {
  /** Short heading, e.g. "Email" */
  label: string;
  /** Shown value(s); same case-insensitive → single value, else "a · b" */
  display: string;
}

export interface DuplicatePairRow {
  pairKind: DuplicatePairKind;
  primaryPatient: DuplicateMatchPatientPayload;
  otherPatient: DuplicateMatchPatientPayload;
  matchedLabels: string[];
  /** One entry per matched field with human-readable values (may be absent on stale/worker rows) */
  matchedDetails?: DuplicateMatchDetail[];
  confidence: number;
  confidenceBand: 'High' | 'Medium' | 'Low';
}

interface ScoreResult {
  score: number;
  criteriaCount: number;
  matchedLabels: string[];
  pairKind: DuplicatePairKind;
  meetsThreshold: boolean;
}

const MATCH_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  surname: 'Surname',
  email: 'Email',
  'mobile number': 'Mobile',
  gender: 'Gender',
  'date of birth': 'Date of birth',
  'id number': 'ID number',
  'passport number': 'Passport number',
};

/** UI label for a matcher field key (e.g. `mobile number` → "Mobile"). */
export function duplicateMatchFieldLabel(fieldKey: string): string {
  return MATCH_FIELD_LABELS[fieldKey] ?? fieldKey;
}

/** Raw display value for one patient on a matched field. */
export function getDuplicateMatchFieldValue(
  patient: DuplicateMatchPatientPayload | undefined | null,
  fieldKey: string,
): string {
  if (!patient) {
    return '—';
  }
  let raw: string;
  switch (fieldKey) {
    case 'name':
      raw = String(patient.name ?? '').trim();
      break;
    case 'surname':
      raw = String(patient.surname ?? '').trim();
      break;
    case 'email':
      raw = String(patient.email ?? '').trim();
      break;
    case 'mobile number':
      raw = String(patient.mobileNumber ?? '').trim();
      break;
    case 'gender':
      raw = String(patient.gender ?? '').trim();
      break;
    case 'date of birth':
      raw = String(patient.dob ?? '').trim();
      break;
    case 'id number':
      raw = String(patient.idNumber ?? '').trim();
      break;
    case 'passport number':
      raw = String(patient.passportNumber ?? '').trim();
      break;
    default:
      return '—';
  }
  return raw || '—';
}

function formatPairFieldDisplay(a: unknown, b: unknown): string {
  const va = String(a ?? '').trim();
  const vb = String(b ?? '').trim();
  if (!va && !vb) {
    return '—';
  }
  if (va.toLowerCase() === vb.toLowerCase()) {
    return va || vb;
  }
  return `${va || '—'} · ${vb || '—'}`;
}

function buildMatchedDetails(
  primary: DuplicateMatchPatientPayload,
  other: DuplicateMatchPatientPayload,
  matchedLabels: string[],
): DuplicateMatchDetail[] {
  return matchedLabels.map((key) => {
    let display: string;
    switch (key) {
      case 'name':
        display = formatPairFieldDisplay(primary.name, other.name);
        break;
      case 'surname':
        display = formatPairFieldDisplay(primary.surname, other.surname);
        break;
      case 'email':
        display = formatPairFieldDisplay(primary.email, other.email);
        break;
      case 'mobile number':
        display = formatPairFieldDisplay(primary.mobileNumber, other.mobileNumber);
        break;
      case 'gender':
        display = formatPairFieldDisplay(primary.gender, other.gender);
        break;
      case 'date of birth':
        display = formatPairFieldDisplay(primary.dob, other.dob);
        break;
      case 'id number':
        display = formatPairFieldDisplay(primary.idNumber, other.idNumber);
        break;
      case 'passport number':
        display = formatPairFieldDisplay(primary.passportNumber, other.passportNumber);
        break;
      default:
        display = '—';
    }
    return {
      label: duplicateMatchFieldLabel(key),
      display,
    };
  });
}

const INVITE_THRESHOLD = 0.75;
const FILE_THRESHOLD = 0.65;

function normalizeCreatedAtForPayload(raw: unknown): string | undefined {
  if (raw == null || raw === '') {
    return undefined;
  }
  if (raw instanceof Date) {
    const t = raw.getTime();
    return Number.isNaN(t) ? undefined : raw.toISOString();
  }
  const s = String(raw).trim();
  return s || undefined;
}

/** Fields used for matching + list display/filter. */
export function toDuplicateMatchPayload(p: any): DuplicateMatchPatientPayload {
  if (p == null) {
    return {};
  }
  return {
    id: p.id,
    active: p.active,
    status: p.status,
    name: p.name,
    surname: p.surname,
    email: p.email,
    mobileNumber: p.mobileNumber,
    dob: p.dob != null ? String(p.dob) : undefined,
    gender: p.gender,
    patientNumber: p.patientNumber,
    diagnosis: p.diagnosis,
    idNumber: p.idNumber,
    passportNumber: p.passportNumber,
    createdAt: normalizeCreatedAtForPayload(p.createdAt),
  };
}

/** Same year+month as moment file–file check for formatted patients (DD/MM/YYYY or ISO date prefix). */
export function dobSameYearMonth(a: unknown, b: unknown): boolean {
  const pa = parseDobYearMonth(a);
  const pb = parseDobYearMonth(b);
  if (!pa || !pb) {
    return false;
  }
  return pa.y === pb.y && pa.m === pb.m;
}

function parseDobYearMonth(dob: unknown): { y: number; m: number } | null {
  if (dob == null || dob === '') {
    return null;
  }
  const s = String(dob).trim();

  const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (dmy) {
    const d = parseInt(dmy[1], 10);
    const m = parseInt(dmy[2], 10);
    const y = parseInt(dmy[3], 10);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return { y, m };
    }
  }

  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) {
    const y = parseInt(iso[1], 10);
    const m = parseInt(iso[2], 10);
    if (m >= 1 && m <= 12) {
      return { y, m };
    }
  }

  return null;
}

/** Normalize ID / passport strings for comparison (spacing and dash variants). */
function normalizeIdentityDocForCompare(s: unknown): string {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-]/g, '');
}

function identityDocsMatch(a: unknown, b: unknown): boolean {
  const na = normalizeIdentityDocForCompare(a);
  const nb = normalizeIdentityDocForCompare(b);
  if (!na || !nb) {
    return false;
  }
  return compareStringsLevenshtein(na, nb);
}

export function compareStringsLevenshtein(str1: string, str2: string): boolean {
  str1 = str1?.toLowerCase() || '';
  str2 = str2?.toLowerCase() || '';

  const matrix: number[][] = [];
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1)
        );
      }
    }
  }
  const distance = matrix[str2.length][str1.length];
  const threshold = Math.max(str1.length, str2.length) / 4;
  return distance <= threshold;
}

function scoreInviteVsFile(invite: DuplicateMatchPatientPayload, file: DuplicateMatchPatientPayload): ScoreResult {
  let similarityScore = 0;
  let criteriaCount = 0;
  const matchedLabels: string[] = [];

  if (invite.name != null && String(invite.name).trim() !== '') {
    if (compareStringsLevenshtein(file.name ?? '', invite.name)) {
      similarityScore++;
      matchedLabels.push('name');
    }
    criteriaCount++;
  }
  if (invite.surname != null && String(invite.surname).trim() !== '') {
    if (compareStringsLevenshtein(file.surname ?? '', invite.surname)) {
      similarityScore++;
      matchedLabels.push('surname');
    }
    criteriaCount++;
  }
  if (invite.email != null && String(invite.email).trim() !== '') {
    if (compareStringsLevenshtein(file.email ?? '', invite.email)) {
      similarityScore += 2;
      matchedLabels.push('email');
    }
    criteriaCount += 2;
  }
  if (invite.mobileNumber != null && String(invite.mobileNumber).trim() !== '') {
    if (compareStringsLevenshtein(file.mobileNumber ?? '', invite.mobileNumber)) {
      similarityScore++;
      matchedLabels.push('mobile number');
    }
    criteriaCount++;
  }
  if (invite.idNumber != null && String(invite.idNumber).trim() !== '') {
    if (identityDocsMatch(file.idNumber, invite.idNumber)) {
      similarityScore += 2;
      matchedLabels.push('id number');
    }
    criteriaCount += 2;
  }
  if (invite.passportNumber != null && String(invite.passportNumber).trim() !== '') {
    if (identityDocsMatch(file.passportNumber, invite.passportNumber)) {
      similarityScore += 2;
      matchedLabels.push('passport number');
    }
    criteriaCount += 2;
  }

  const ratio = criteriaCount > 0 ? similarityScore / criteriaCount : 0;
  return {
    score: similarityScore,
    criteriaCount,
    matchedLabels,
    pairKind: 'invite-file',
    meetsThreshold: criteriaCount > 0 && ratio >= INVITE_THRESHOLD
  };
}

function scoreInviteInvite(a: DuplicateMatchPatientPayload, b: DuplicateMatchPatientPayload): ScoreResult {
  let similarityScore = 0;
  let criteriaCount = 0;
  const matchedLabels: string[] = [];

  const nameA = a.name != null && String(a.name).trim() !== '';
  const nameB = b.name != null && String(b.name).trim() !== '';
  if (nameA && nameB) {
    if (compareStringsLevenshtein(a.name ?? '', b.name ?? '')) {
      similarityScore++;
      matchedLabels.push('name');
    }
    criteriaCount++;
  }

  const surA = a.surname != null && String(a.surname).trim() !== '';
  const surB = b.surname != null && String(b.surname).trim() !== '';
  if (surA && surB) {
    if (compareStringsLevenshtein(a.surname ?? '', b.surname ?? '')) {
      similarityScore++;
      matchedLabels.push('surname');
    }
    criteriaCount++;
  }

  const emA = a.email != null && String(a.email).trim() !== '';
  const emB = b.email != null && String(b.email).trim() !== '';
  if (emA && emB) {
    if (compareStringsLevenshtein(a.email ?? '', b.email ?? '')) {
      similarityScore += 2;
      matchedLabels.push('email');
    }
    criteriaCount += 2;
  }

  const mobA = a.mobileNumber != null && String(a.mobileNumber).trim() !== '';
  const mobB = b.mobileNumber != null && String(b.mobileNumber).trim() !== '';
  if (mobA && mobB) {
    if (compareStringsLevenshtein(a.mobileNumber ?? '', b.mobileNumber ?? '')) {
      similarityScore++;
      matchedLabels.push('mobile number');
    }
    criteriaCount++;
  }

  const idA = a.idNumber != null && String(a.idNumber).trim() !== '';
  const idB = b.idNumber != null && String(b.idNumber).trim() !== '';
  if (idA && idB) {
    if (identityDocsMatch(a.idNumber, b.idNumber)) {
      similarityScore += 2;
      matchedLabels.push('id number');
    }
    criteriaCount += 2;
  }

  const passA = a.passportNumber != null && String(a.passportNumber).trim() !== '';
  const passB = b.passportNumber != null && String(b.passportNumber).trim() !== '';
  if (passA && passB) {
    if (identityDocsMatch(a.passportNumber, b.passportNumber)) {
      similarityScore += 2;
      matchedLabels.push('passport number');
    }
    criteriaCount += 2;
  }

  const ratio = criteriaCount > 0 ? similarityScore / criteriaCount : 0;
  return {
    score: similarityScore,
    criteriaCount,
    matchedLabels,
    pairKind: 'invite-invite',
    meetsThreshold: criteriaCount > 0 && ratio >= INVITE_THRESHOLD
  };
}

function scoreFileFile(a: DuplicateMatchPatientPayload, b: DuplicateMatchPatientPayload): ScoreResult {
  let similarityScore = 0;
  let criteriaCount = 0;
  const matchedLabels: string[] = [];

  if (compareStringsLevenshtein(a.name ?? '', b.name ?? '')) {
    similarityScore++;
    matchedLabels.push('name');
  }
  criteriaCount++;

  if (compareStringsLevenshtein(a.surname ?? '', b.surname ?? '')) {
    similarityScore++;
    matchedLabels.push('surname');
  }
  criteriaCount++;

  if (a.dob && b.dob) {
    if (dobSameYearMonth(a.dob, b.dob)) {
      similarityScore++;
      matchedLabels.push('date of birth');
    }
    criteriaCount++;
  }

  if (a.gender && b.gender) {
    if (a.gender === b.gender) {
      similarityScore++;
      matchedLabels.push('gender');
    }
    criteriaCount++;
  }

  const idFA = a.idNumber != null && String(a.idNumber).trim() !== '';
  const idFB = b.idNumber != null && String(b.idNumber).trim() !== '';
  if (idFA && idFB) {
    if (identityDocsMatch(a.idNumber, b.idNumber)) {
      similarityScore += 2;
      matchedLabels.push('id number');
    }
    criteriaCount += 2;
  }

  const passFA = a.passportNumber != null && String(a.passportNumber).trim() !== '';
  const passFB = b.passportNumber != null && String(b.passportNumber).trim() !== '';
  if (passFA && passFB) {
    if (identityDocsMatch(a.passportNumber, b.passportNumber)) {
      similarityScore += 2;
      matchedLabels.push('passport number');
    }
    criteriaCount += 2;
  }

  const ratio = criteriaCount > 0 ? similarityScore / criteriaCount : 0;
  return {
    score: similarityScore,
    criteriaCount,
    matchedLabels,
    pairKind: 'file-file',
    meetsThreshold: criteriaCount > 0 && ratio >= FILE_THRESHOLD
  };
}

function evaluatePair(a: DuplicateMatchPatientPayload, b: DuplicateMatchPatientPayload): ScoreResult | null {
  const aInvite = a?.status === 'invite';
  const bInvite = b?.status === 'invite';

  if (aInvite && bInvite) {
    return scoreInviteInvite(a, b);
  }
  if (aInvite && !bInvite) {
    return scoreInviteVsFile(a, b);
  }
  if (!aInvite && bInvite) {
    return scoreInviteVsFile(b, a);
  }
  return scoreFileFile(a, b);
}

function toRow(
  p1: DuplicateMatchPatientPayload,
  p2: DuplicateMatchPatientPayload,
  s: ScoreResult
): DuplicatePairRow {
  const ratio = s.criteriaCount > 0 ? s.score / s.criteriaCount : 0;
  const confidence = Math.round(ratio * 100);
  const confidenceBand: DuplicatePairRow['confidenceBand'] =
    confidence >= 90 ? 'High' : confidence >= 70 ? 'Medium' : 'Low';

  let primary: DuplicateMatchPatientPayload;
  let other: DuplicateMatchPatientPayload;

  if (s.pairKind === 'invite-file') {
    const invite = p1.status === 'invite' ? p1 : p2;
    const file = invite === p1 ? p2 : p1;
    primary = invite;
    other = file;
  } else {
    const id1 = String(p1.id ?? '');
    const id2 = String(p2.id ?? '');
    if (id1 <= id2) {
      primary = p1;
      other = p2;
    } else {
      primary = p2;
      other = p1;
    }
  }

  return {
    pairKind: s.pairKind,
    primaryPatient: primary,
    otherPatient: other,
    matchedLabels: [...s.matchedLabels],
    matchedDetails: buildMatchedDetails(primary, other, s.matchedLabels),
    confidence,
    confidenceBand
  };
}

export function computeDuplicatePairsCore(
  patients: DuplicateMatchPatientPayload[] | null | undefined
): DuplicatePairRow[] {
  const list = patients ?? [];
  const active = list.filter(p => p.active === true);
  const rows: DuplicatePairRow[] = [];

  for (let i = 0; i < active.length; i++) {
    for (let j = i + 1; j < active.length; j++) {
      const scored = evaluatePair(active[i], active[j]);
      if (scored?.meetsThreshold) {
        rows.push(toRow(active[i], active[j], scored));
      }
    }
  }
  return rows;
}

/**
 * Fills `matchedDetails` when missing (e.g. row from an older web worker bundle).
 */
export function normalizeDuplicatePairRow(row: DuplicatePairRow): DuplicatePairRow {
  if (row.matchedDetails?.length) {
    return row;
  }
  return {
    ...row,
    matchedDetails: buildMatchedDetails(
      row.primaryPatient,
      row.otherPatient,
      row.matchedLabels ?? [],
    ),
  };
}
